import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  TenantCase,
  TenantFinalDocument,
  fetchTenantCase,
  fetchTenantCases,
  fetchTenantDocumentUrl,
  recordOfflinePayment,
  withdrawOfflinePayment,
} from '../../api/tenantPortal';
import { isComplete, parseTimestamp, statusLabel, timeInStep } from './status';
import './TenantCasesPage.css';

/**
 * What a white-label partner sees: their own cases, and a download for every
 * NIF we have finished.
 *
 * Note this screen is Coepi-branded, unlike the customer-facing app. The
 * partner signed a contract with us and knows exactly who we are. The white
 * label protects *their customers* from seeing our brand, not them.
 *
 * A partner whose clients pay by card also gets a Payment column: a client who
 * cannot pay by card pays the partner directly, and an admin marks the case
 * paid here so the client can continue (lawyer-backend offline_payments.py).
 * Coepi then invoices the partner for that case.
 */

interface Props {
  /** The partner's clients pay by card (no access codes), so the Payment column applies. */
  clientsPay?: boolean;
  /** Admins record payments; members only see them. */
  isAdmin?: boolean;
}

const formatDate = (value: any): string => {
  const ms = parseTimestamp(value);
  return ms === null ? '' : new Date(ms).toLocaleDateString();
};

const TenantCasesPage: React.FC<Props> = ({ clientsPay = false, isAdmin = false }) => {
  const { currentUser } = useAuth();
  const [cases, setCases] = useState<TenantCase[]>([]);
  const [tenantName, setTenantName] = useState('');
  const [docs, setDocs] = useState<Record<string, TenantFinalDocument[]>>({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!currentUser) return;
      try {
        const token = await currentUser.getIdToken();
        const data = await fetchTenantCases(token);
        if (cancelled) return;
        // Defensive: a partner losing the whole page to a malformed payload
        // is a worse failure than an empty table.
        const caseList = data?.cases ?? [];
        setCases(caseList);
        setTenantName(data?.tenantName ?? '');

        // Fetch the finished documents for completed cases only. The partner
        // is here to collect NIFs, so the download has to be one click from
        // the list rather than behind a detail page.
        const completed = caseList.filter(isComplete);
        const entries = await Promise.all(
          completed.map(async (c) => {
            try {
              const detail = await fetchTenantCase(token, c.id);
              return [c.id, detail.final_documents] as const;
            } catch {
              // One unreadable case must not blank the whole list.
              return [c.id, []] as const;
            }
          })
        );
        if (!cancelled) setDocs(Object.fromEntries(entries));
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Could not load your cases.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [currentUser]);

  const setOffline = (caseId: string, status: TenantCase['offline_payment_status']) =>
    setCases((list) => list.map((c) => (c.id === caseId ? { ...c, offline_payment_status: status } : c)));

  const markPaid = async (c: TenantCase) => {
    if (!currentUser) return;
    const who = c.user_display_name || c.user_email || 'this client';
    // One dialog that both confirms and asks how they paid. Cancel: nothing happens.
    const note = window.prompt(
      `Mark ${who} as paid?\n\nOnly when they paid you directly. Their application continues, and Coepi invoices you for it.\n\nHow did they pay? (optional)`,
      ''
    );
    if (note === null) return;
    setBusy(`pay:${c.id}`);
    setError('');
    try {
      await recordOfflinePayment(await currentUser.getIdToken(), c.id, note.trim());
      setOffline(c.id, 'recorded');
    } catch (e: any) {
      setError(e?.message || 'Could not mark this client as paid.');
    } finally {
      setBusy(null);
    }
  };

  const undoPaid = async (c: TenantCase) => {
    if (!currentUser) return;
    setBusy(`pay:${c.id}`);
    setError('');
    try {
      await withdrawOfflinePayment(await currentUser.getIdToken(), c.id);
      setOffline(c.id, 'revoked');
    } catch (e: any) {
      setError(e?.message || 'Could not undo this payment.');
    } finally {
      setBusy(null);
    }
  };

  const payment = (c: TenantCase) => {
    if (c.offline_payment_status === 'settled') return <span>Paid to you</span>;
    if (c.paid) return <span>Paid</span>;
    if (c.offline_payment_status === 'recorded') {
      return (
        <>
          <span>Marked as paid</span>
          {isAdmin && (
            <button type="button" className="tenant-download tenant-pay-undo" disabled={busy === `pay:${c.id}`} onClick={() => undoPaid(c)}>
              Undo
            </button>
          )}
        </>
      );
    }
    if (isComplete(c)) return <span className="tenant-empty">Paid</span>;
    return isAdmin ? (
      <button type="button" className="tenant-download" disabled={busy === `pay:${c.id}`} onClick={() => markPaid(c)}>
        {busy === `pay:${c.id}` ? 'Saving...' : 'Mark as paid'}
      </button>
    ) : (
      <span className="tenant-empty">Not paid yet</span>
    );
  };

  const download = useCallback(
    async (caseId: string, fileName: string) => {
      if (!currentUser) return;
      setBusy(`${caseId}:${fileName}`);
      setError('');
      try {
        const token = await currentUser.getIdToken();
        const url = await fetchTenantDocumentUrl(token, caseId, fileName);
        // Signed URL, valid for 15 minutes. Opened rather than fetched so the
        // browser handles the download and the link never lands in our logs.
        window.open(url, '_blank', 'noopener,noreferrer');
      } catch (e: any) {
        setError(e?.message || 'Could not prepare that download.');
      } finally {
        setBusy(null);
      }
    },
    [currentUser]
  );

  if (loading) return <div className="tenant-cases">Loading your cases...</div>;

  return (
    <div className="tenant-cases">
      <h2>Your cases</h2>
      <p className="tenant-subtitle">
        {tenantName ? `${tenantName} ` : ''}NIF applications and completed documents.
      </p>

      {error && <p className="tenant-error">{error}</p>}

      {cases.length === 0 ? (
        <p className="tenant-empty">No cases yet.</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Client</th>
              <th>Started</th>
              <th>Status</th>
              {clientsPay && <th>Payment</th>}
              <th>Documents</th>
            </tr>
          </thead>
          <tbody>
            {cases.map((c) => {
              const done = isComplete(c);
              const files = docs[c.id] || [];
              const inStep = timeInStep(c);
              const sinceMs = parseTimestamp(c.status_since);
              return (
                <tr key={c.id}>
                  <td>{c.user_display_name || c.user_email || c.id}</td>
                  <td>{formatDate(c.created_at)}</td>
                  <td>
                    <span className={`tenant-status${done ? ' tenant-status--done' : ''}`}>
                      {statusLabel(c)}
                    </span>
                    {inStep && (
                      <span
                        className="tenant-status-age"
                        title={sinceMs === null ? undefined : `Since ${new Date(sinceMs).toLocaleString()}`}
                      >
                        for {inStep}
                      </span>
                    )}
                  </td>
                  {clientsPay && <td>{payment(c)}</td>}
                  <td>
                    {!done ? (
                      <span className="tenant-empty">Not ready yet</span>
                    ) : files.length === 0 ? (
                      <span className="tenant-empty">No documents</span>
                    ) : (
                      files.map((f) => (
                        <div key={f.id}>
                          <button
                            type="button"
                            className="tenant-download"
                            disabled={!f.fileName || busy === `${c.id}:${f.fileName}`}
                            onClick={() => f.fileName && download(c.id, f.fileName)}
                          >
                            {busy === `${c.id}:${f.fileName}`
                              ? 'Preparing...'
                              : f.fileName || 'Document'}
                          </button>
                        </div>
                      ))
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default TenantCasesPage;
